<template>
  <div
    class="flex flex-col w-full"
    :class="{'mb-[200px]': blockedCountries.length > 50}"
  >
    <h1 class="text-xl font-semibold text-black mb-0">
      Traffic Manager
    </h1>

    <div class="bg-white w-full border border-[#D7D7D7] shadow-[0_1px_3px_0_rgba(0,0,0,0.1)] p-8 rounded-[5px] mt-3">
      <div
        v-if="initLoading"
        class="w-full h-full flex items-center justify-center"
      >
        <b-spinner
          class="ml-2"
          variant="primary"
          size="xl"
        />
      </div>

      <template v-else>
        <world-map-select v-model="blockedCountries" />

        <div class="flex flex-col mt-[20px]">
          <template v-if="isAws">
            <h3 class="text-base text-black font-semibold">
              GEO-BLOCK Countries Presets:
            </h3>

            <p class="text-sm text-[#6E747A] mb-1 startcase">
              Select the country your hub is located in:
            </p>

            <v-select
              v-model="preset"
              :options="presets"
              label="name"
              :reduce="item => item.value"
              class="custom-select mt-[10px]"
              :readonly="!canUpdateDomainManagement"
              searchable
              @option:selected="setPreset"
            />
          </template>

          <h3 class="text-base text-black font-semibold mt-4">
            Blocked countries
          </h3>

          <p
            v-if="!isAws"
            class="text-sm font-semibold text-[#6E747A] mb-1"
          >
            You can block up to 5 countries.
          </p>

          <p class="text-sm text-[#6E747A] mb-1">
            Traffic connecting from a blocked country will be rejected on the DNS level and will not reach our servers at all.
          </p>

          <div class="relative w-full flex gap-[10px] mt-[10px]">
            <v-select
              v-model="blockedCountries"
              :options="countriesOptions"
              class="custom-select !px-[1px] !pb-[3px] flex-1"
              :limit="isAws ? undefined : 5"
              label="name"
              multiple
              :reduce="item => item.iso2"
              :clearable="true"
              placeholder="Select countries..."
              :readonly="!canUpdateDomainManagement"
              searchable
            >
              <template #selected-option="option">
                <div class="d-flex align-items-center px-[2px] py-1">
                  <div>
                    {{ option.emoji }}
                    {{ option.name }}
                  </div>
                </div>
              </template>

              <template #option="option">
                <div class="d-flex align-items-center">
                  <div>
                    {{ option.emoji }}
                    {{ option.name }}
                  </div>
                </div>
              </template>
            </v-select>

            <b-btn
              v-if="canUpdateDomainManagement"
              variant="light"
              size="sm"
              class="shrink-0 h-[36px] !px-3 border border-[#D7D7D7]"
              :disabled="!blockedCountries || !blockedCountries.length"
              @click="clearBlockedCountries"
            >
              Clear All
            </b-btn>
          </div>
        </div>
      </template>
    </div>

    <div>
      <b-btn
        variant="primary"
        class="mt-3 h-[40px]"
        :disabled="initLoading || loading"
        @click="submit"
      >
        Save Country Filters
        <b-spinner
          v-if="loading"
          class="ml-2"
          variant="white"
          small
        />
      </b-btn>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from 'vuex'
import WorldMapSelect from '@/views/domain/views/WorldMapSelect.vue'
export default {
  name: 'DomainTraffic',
  components: {
    WorldMapSelect
  },
  data () {
    return {
      loading: false,
      initLoading: false,
      presets: [
        {
          name: 'United States',
          value: 'US',
          available_list: [
            { name: 'United States', code: 'US' },
            { name: 'Belgium', code: 'BE' },
            { name: 'Finland', code: 'FI' },
            { name: 'Lithuania', code: 'LT' },
            { name: 'Portugal', code: 'PT' },
            { name: 'Bulgaria', code: 'BG' },
            { name: 'Greece', code: 'GR' },
            { name: 'Italy', code: 'IT' },
            { name: 'Romania', code: 'RO' },
            { name: 'Croatia', code: 'HR' },
            { name: 'Hungary', code: 'HU' },
            { name: 'Malta', code: 'MT' },
            { name: 'Slovakia', code: 'SK' },
            { name: 'Cyprus', code: 'CY' },
            { name: 'Iceland', code: 'IS' },
            { name: 'Netherlands', code: 'NL' },
            { name: 'Slovenia', code: 'SI' },
            { name: 'Czechia', code: 'CZ' },
            { name: 'Latvia', code: 'LV' },
            { name: 'Norway', code: 'NO' },
            { name: 'Spain', code: 'ES' },
            { name: 'Denmark', code: 'DK' },
            { name: 'Liechtenstein', code: 'LI' },
            { name: 'Poland', code: 'PL' },
            { name: 'Sweden', code: 'SE' },
            { name: 'Estonia', code: 'EE' },
            { name: 'France', code: 'FR' },
            { name: 'Germany', code: 'DE' },
            { name: 'Ireland', code: 'IE' },
            { name: 'Austria', code: 'AT' },
            { name: 'United Kingdom (England)', code: 'GB' },
            { name: 'United Kingdom (Wales)', code: 'GB' },
            { name: 'United Kingdom (Northern Ireland)', code: 'GB' },
            { name: 'United Kingdom (Scotland)', code: 'GB' },
            { name: 'Switzerland', code: 'CH' },
            { name: 'Canada', code: 'CA' },
            { name: 'Israel', code: 'IL' },
            { name: 'Australia', code: 'AU' },
            { name: 'New Zealand', code: 'NZ' },
            { name: 'India', code: 'IN' }
          ]
        },
        {
          name: 'EU (except France, Germany, Ireland, Austria)',
          value: 'EU',
          available_list: [
            { name: 'Canada', code: 'CA' },
            { name: 'United States', code: 'US' },
            { name: 'Belgium', code: 'BE' },
            { name: 'Finland', code: 'FI' },
            { name: 'Lithuania', code: 'LT' },
            { name: 'Portugal', code: 'PT' },
            { name: 'Bulgaria', code: 'BG' },
            { name: 'Greece', code: 'GR' },
            { name: 'Italy', code: 'IT' },
            { name: 'Romania', code: 'RO' },
            { name: 'Croatia', code: 'HR' },
            { name: 'Hungary', code: 'HU' },
            { name: 'Malta', code: 'MT' },
            { name: 'Slovakia', code: 'SK' },
            { name: 'Cyprus', code: 'CY' },
            { name: 'Iceland', code: 'IS' },
            { name: 'Netherlands', code: 'NL' },
            { name: 'Slovenia', code: 'SI' },
            { name: 'Czechia', code: 'CZ' },
            { name: 'Latvia', code: 'LV' },
            { name: 'Norway', code: 'NO' },
            { name: 'Spain', code: 'ES' },
            { name: 'Denmark', code: 'DK' },
            { name: 'Liechtenstein', code: 'LI' },
            { name: 'Poland', code: 'PL' },
            { name: 'Sweden', code: 'SE' },
            { name: 'Estonia', code: 'EE' },
            { name: 'United Kingdom (England)', code: 'GB' },
            { name: 'United Kingdom (Wales)', code: 'GB' },
            { name: 'United Kingdom (Northern Ireland)', code: 'GB' },
            { name: 'United Kingdom (Scotland)', code: 'GB' }
          ]
        },
        {
          name: 'France',
          value: 'FR',
          available_list: [
            { name: 'United States', code: 'US' },
            { name: 'France', code: 'FR' }
          ]
        },
        {
          name: 'Germany',
          value: 'DE',
          available_list: [
            { name: 'United States', code: 'US' },
            { name: 'Germany', code: 'DE' }
          ]
        },
        {
          name: 'Ireland',
          value: 'IE',
          available_list: [
            { name: 'United States', code: 'US' },
            { name: 'Ireland', code: 'IE' }
          ]
        },
        {
          name: 'Austria',
          value: 'AT',
          available_list: [
            { name: 'United States', code: 'US' },
            { name: 'Austria', code: 'AT' }
          ]
        },
        {
          name: 'United Kingdom',
          value: 'GB',
          available_list: [
            { name: 'United States', code: 'US' },
            { name: 'United Kingdom (England)', code: 'GB' },
            { name: 'United Kingdom (Wales)', code: 'GB' },
            { name: 'United Kingdom (Northern Ireland)', code: 'GB' },
            { name: 'United Kingdom (Scotland)', code: 'GB' },
            { name: 'Belgium', code: 'BE' },
            { name: 'Finland', code: 'FI' },
            { name: 'Lithuania', code: 'LT' },
            { name: 'Portugal', code: 'PT' },
            { name: 'Bulgaria', code: 'BG' },
            { name: 'Greece', code: 'GR' },
            { name: 'Italy', code: 'IT' },
            { name: 'Romania', code: 'RO' },
            { name: 'Croatia', code: 'HR' },
            { name: 'Hungary', code: 'HU' },
            { name: 'Malta', code: 'MT' },
            { name: 'Slovakia', code: 'SK' },
            { name: 'Cyprus', code: 'CY' },
            { name: 'Iceland', code: 'IS' },
            { name: 'Netherlands', code: 'NL' },
            { name: 'Slovenia', code: 'SI' },
            { name: 'Czechia', code: 'CZ' },
            { name: 'Latvia', code: 'LV' },
            { name: 'Norway', code: 'NO' },
            { name: 'Spain', code: 'ES' },
            { name: 'Denmark', code: 'DK' },
            { name: 'Liechtenstein', code: 'LI' },
            { name: 'Poland', code: 'PL' },
            { name: 'Sweden', code: 'SE' },
            { name: 'Estonia', code: 'EE' },
            { name: 'Canada', code: 'CA' }
          ]
        },
        {
          name: 'Switzerland',
          value: 'GB',
          available_list: [
            { name: 'United States', code: 'US' },
            { name: 'United Kingdom', code: 'GB' }
          ]
        },
        {
          name: 'Canada',
          value: 'CA',
          available_list: [
            { name: 'United States', code: 'US' },
            { name: 'Canada', code: 'CA' },
            { name: 'Belgium', code: 'BE' },
            { name: 'Finland', code: 'FI' },
            { name: 'Lithuania', code: 'LT' },
            { name: 'Portugal', code: 'PT' },
            { name: 'Bulgaria', code: 'BG' },
            { name: 'Greece', code: 'GR' },
            { name: 'Italy', code: 'IT' },
            { name: 'Romania', code: 'RO' },
            { name: 'Croatia', code: 'HR' },
            { name: 'Hungary', code: 'HU' },
            { name: 'Malta', code: 'MT' },
            { name: 'Slovakia', code: 'SK' },
            { name: 'Cyprus', code: 'CY' },
            { name: 'Iceland', code: 'IS' },
            { name: 'Netherlands', code: 'NL' },
            { name: 'Slovenia', code: 'SI' },
            { name: 'Czechia', code: 'CZ' },
            { name: 'Latvia', code: 'LV' },
            { name: 'Norway', code: 'NO' },
            { name: 'Spain', code: 'ES' },
            { name: 'Denmark', code: 'DK' },
            { name: 'Liechtenstein', code: 'LI' },
            { name: 'Poland', code: 'PL' },
            { name: 'Sweden', code: 'SE' },
            { name: 'Estonia', code: 'EE' },
            { name: 'United Kingdom (England)', code: 'GB' },
            { name: 'United Kingdom (Wales)', code: 'GB' },
            { name: 'United Kingdom (Northern Ireland)', code: 'GB' },
            { name: 'United Kingdom (Scotland)', code: 'GB' }
          ]
        },
        {
          name: 'India',
          value: 'IN',
          available_list: [
            { name: 'United States', code: 'US' },
            { name: 'India', code: 'IN' }
          ]
        },
        {
          name: 'Israel',
          value: 'IL',
          available_list: [
            { name: 'United States', code: 'US' },
            { name: 'Israel', code: 'IL' }
          ]
        },
        {
          name: 'Australia',
          value: 'AU',
          available_list: [
            { name: 'United States', code: 'US' },
            { name: 'Australia', code: 'AU' }
          ]
        },
        {
          name: 'New Zealand',
          value: 'NZ',
          available_list: [
            { name: 'United States', code: 'US' },
            { name: 'New Zealand', code: 'NZ' }
          ]
        }
      ],
      countriesOptions: [],
      blockedCountries: [],
      guid: null
    }
  },
  computed: {
    ...mapState('projects', {
      project: state => state.project
    }),
    ...mapState('auth', {
      user: state => state.user
    }),
    ...mapGetters('projects', {
      checkPermissions: 'checkPermissions'
    }),
    canUpdateDomainManagement () {
      return this.checkPermissions('domain_management.update')
    },
    preset: {
      get () {
        return this.project.geo_block_preset
      },
      set (val) {
        this.$set(this.project, 'geo_block_preset', val)
      }
    },
    isAws () {
      return !!this.project.aws_data?.tenant_id
    }
  },
  methods: {
    ...mapActions({
      fetchCountries: 'domain/fetchCountries',
      fetchBlockedCountries: 'domain/fetchBlockedCountries',
      blockCountries: 'domain/blockCountries'
    }),
    iso2ToFlag (code) {
      if (!code || code.length !== 2) return ''
      const base = 127397 // 'REGIONAL INDICATOR SYMBOL LETTER A' - 'A'
      return code.toUpperCase().replace(/./g, c => String.fromCodePoint(base + c.charCodeAt(0)))
    },
    clearBlockedCountries () {
      this.blockedCountries = []
      this.preset = null
    },
    setPreset (preset) {
      const currentPreset = this.presets.find(item => item.value === preset.value)
      const availableList = currentPreset.available_list.map(item => item.code)
      const allCountries = this.countriesOptions.map(item => item.iso2)
      this.blockedCountries = allCountries.filter(item => !availableList.includes(item))
    },
    async submit () {
      this.loading = true
      const payload = {
        countries: this.blockedCountries.length >= 1 ? this.blockedCountries : undefined,
        guid: this.guid || undefined
      }
      if (this.isAws) {
        const newAPI = 'https://new-api.estage.com'
        VEvent.fire('loader', true)
        try {
          await axios.post(`${newAPI}/api/projects/${this.project.id}/cloud-front/traffic-manager`, {
            countries: payload.countries
          })
            .then(() => {
              this.$swal({
                icon: 'success',
                title: 'Blocked countries successfully',
                showConfirmButton: false,
                timer: 1500
              })
            })

          await this.$store.dispatch('projects/save', {}, {root: true})
        } catch (e) {
          console.error(e)
          this.$swal({
            icon: 'error',
            title: 'Server error',
            text: 'Please try again later or let us know about your problem.',
            showConfirmButton: false,
            timer: 5000
          })
        } finally {
          this.loading = false
          VEvent.fire('loader', false)
        }
      } else {
        await this.blockCountries(payload)
          .then(() => {
            this.$swal({
              icon: 'success',
              title: 'Blocked countries successfully',
              showConfirmButton: false,
              timer: 1500
            })
          })
          .catch((error) => {
            console.log(error)
            this.$swal({
              icon: 'error',
              iconColor: '#e34f5b',
              toast: true,
              position: 'top-right',
              title: 'Something went wrong, please try again.',
              showConfirmButton: false,
              timer: 5000
            })
          })
          .finally(() => this.loading = false)
      }
    }
  },
  async mounted () {
    this.initLoading = true

    await this.$store.dispatch('projects/hasAccessToPage', 'domain_management')

    // await axios.get('api/user/profile/countries')
    //   .then(({data}) => {
    //     this.countriesOptions = data.data
    //   })
    await this.fetchCountries()
      .then(res => {
        this.countriesOptions = res.map(item => ({
          name: item.Name,
          iso2: item.IsoCode === 'IC' ? 'ES' : item.IsoCode,
          emoji: this.iso2ToFlag(item.IsoCode === 'IC' ? 'ES' : item.IsoCode)
        }))
      })
    if (this.isAws) {
      const newAPI = 'https://new-api.estage.com'
      await axios.get(`${newAPI}/api/projects/${this.project.id}/cloud-front/get-tenant`)
        .then(res => {
          if (res.data.data) {
            this.blockedCountries = res.data.data.Customizations?.GeoRestrictions?.Locations ?? []
          }
        })
    } else {
      await this.fetchBlockedCountries()
        .then(res => {
          if (res.triggers) {
            const blocks = res.triggers.find(item => item.Type === 4)
            this.blockedCountries = blocks.PatternMatches
          } else {
            this.blockedCountries = []
          }
          this.guid = res.guid ?? undefined
        })
    }
    this.initLoading = false
  }
}
</script>